import { useEffect, useState } from "react";
import { useGetIdentity } from "react-admin";
import Clarity from "@microsoft/clarity";

const CONSENT_KEY = "clarity-consent";
const clarityProjectId = import.meta.env.VITE_MS_CLARITY_PROJECT_ID;

/**
 * Manages the Clarity consent banner and initializes tracking after user consent.
 *
 * Handles the full consent flow:
 * - Shows consent banner for new users (only if Clarity is configured)
 * - Stores consent preference in localStorage
 * - Initializes Clarity tracking with user identity and tags after consent
 *
 * @example
 * // Used in ClarityConsentBanner component
 * const { showBanner, acceptConsent, declineConsent } = useClarityConsent();
 */
export const useClarityConsent = () => {
  const { data: identity, isLoading } = useGetIdentity();
  const [showBanner, setShowBanner] = useState(false);
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null);

  // Check for existing consent on mount
  useEffect(() => {
    // Don't show banner if Clarity isn't configured
    if (!clarityProjectId) {
      setShowBanner(false);
      return;
    }

    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "true") {
      setConsentGiven(true);
    } else if (stored === "false") {
      setConsentGiven(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  // Enable Clarity when consent is given and user is identified
  useEffect(() => {
    if (consentGiven && !isLoading && identity && clarityProjectId) {
      Clarity.consent();
      Clarity.identify(String(identity.id), undefined, undefined, identity.fullName);

      if (identity.assignedRoles?.[0]?.role?.name) {
        Clarity.setTag("role", identity.assignedRoles[0].role.name);
      }
      if (identity.accountType) {
        Clarity.setTag("accountType", identity.accountType);
      }
    }
  }, [consentGiven, identity, isLoading]);

  const acceptConsent = () => {
    localStorage.setItem(CONSENT_KEY, "true");
    setConsentGiven(true);
    setShowBanner(false);
  };

  const declineConsent = () => {
    localStorage.setItem(CONSENT_KEY, "false");
    setConsentGiven(false);
    setShowBanner(false);
  };

  return { showBanner, acceptConsent, declineConsent };
};

/**
 * Provides read/write access to Clarity consent settings for use in preferences UI.
 *
 * Unlike useClarityConsent, this hook is for settings pages where users can
 * review and change their analytics preference after initial consent decision.
 * Triggers a page reload on change to ensure Clarity picks up the new state.
 *
 * @example
 * // Used in AuthInfoPage for the analytics toggle
 * const { clarityEnabled, consentGiven, updateConsent } = useClarityConsentSettings();
 */
export const useClarityConsentSettings = () => {
  const clarityEnabled = Boolean(import.meta.env.VITE_MS_CLARITY_PROJECT_ID);

  const [consentGiven, setConsentGiven] = useState(() => {
    return localStorage.getItem(CONSENT_KEY) === "true";
  });

  const updateConsent = (value: boolean) => {
    localStorage.setItem(CONSENT_KEY, value ? "true" : "false");
    setConsentGiven(value);
    window.location.reload();
  };

  return { clarityEnabled, consentGiven, updateConsent };
};

/**
 * Sets a custom Clarity tag for filtering sessions in the dashboard.
 *
 * Only sets tags if Clarity is configured and value is provided.
 * For automatic resource/view tracking, use ClarityResourceTracker component instead.
 *
 * @example
 * // Tag a session with a specific workflow
 * useClarityTag("workflow", "claim-submission");
 */
export const useClarityTag = (key: string, value: string) => {
  if (clarityProjectId && value) {
    Clarity.setTag(key, value);
  }
};
